import { CalendarRepetition } from '@models/integration.model';

const getRepeatRule = (
  repeatType: CalendarRepetition,
  repeatEvery: number,
  selectedDays: string[],
  selectedDay: string,
  selectedMonth?: number,
  position?: string | null,
  iterations?: number,
  endDate?: string,
  timezone?: string,
) => {
  let repeat: string | null = '';
  switch (repeatType) {
    case CalendarRepetition.DAILY:
      repeat = `RRULE:FREQ=DAILY;INTERVAL=${repeatEvery};`;
      break;
    case CalendarRepetition.WEEKLY:
      repeat = `RRULE:FREQ=WEEKLY;WKST=MO;INTERVAL=${repeatEvery};BYDAY=${selectedDays.join(',')};`;
      break;
    case CalendarRepetition.MONTHLY:
      if (position) {
        repeat = `RRULE:FREQ=MONTHLY;BYSETPOS=${position};BYDAY=${selectedDay};INTERVAL=${repeatEvery};`;
      } else {
        repeat = `RRULE:FREQ=MONTHLY;BYMONTHDAY=${selectedDay};INTERVAL=${repeatEvery};`;
      }
      break;
    case CalendarRepetition.YEARLY:
      if (position) {
        repeat = `RRULE:FREQ=YEARLY;INTERVAL=${repeatEvery};BYSETPOS=${position};BYDAY=${selectedDay};BYMONTH=${selectedMonth};`;
      } else {
        repeat = `RRULE:FREQ=YEARLY;INTERVAL=${repeatEvery};BYMONTH=${selectedMonth};BYMONTHDAY=${selectedDay};`;
      }
      break;
    case CalendarRepetition.NO_REPEAT:
      repeat = null;
      break;
    default:
      break;
  }

  if (iterations) {
    repeat += `COUNT=${iterations};`;
  }

  if (endDate) {
    try {
      const date = new Date(endDate);
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', endDate);
        return repeat;
      }

      // Set to end of day in the event's timezone, not UTC
      if (timezone) {
        // For timezone-aware events, format as floating time (no Z suffix)
        // Google Calendar will interpret this in the event's timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        repeat += `UNTIL=${year}${month}${day}T235959;`;
      } else {
        // For UTC events, keep the Z suffix
        date.setUTCHours(23, 59, 59, 999);
        const year = date.getUTCFullYear();
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const day = String(date.getUTCDate()).padStart(2, '0');
        const hours = String(date.getUTCHours()).padStart(2, '0');
        const minutes = String(date.getUTCMinutes()).padStart(2, '0');
        const seconds = String(date.getUTCSeconds()).padStart(2, '0');
        repeat += `UNTIL=${year}${month}${day}T${hours}${minutes}${seconds}Z;`;
      }
    } catch (error) {
      console.error('Error formatting date:', error);
      return repeat;
    }
  }

  return repeat;
};

export default getRepeatRule;

export const translateRepeatRule = (rrule: string) => {
  if (!rrule) {
    return null;
  }
  const values = rrule.replace('RRULE:', '').split(';');

  let repeatType = values[0];
  let repeatEvery: number = 1;
  let selectedDays: string[] = ['1'];
  let selectedDay: string = '';
  let selectedMonth: number = 1;
  let position: string = '';
  const hasPosition = rrule.includes('BYSETPOS');

  switch (repeatType) {
    case 'FREQ=DAILY':
      repeatType = CalendarRepetition.DAILY;
      repeatEvery = Number(values.find((value) => value.includes('INTERVAL'))?.replace('INTERVAL=', '')) || 1;
      break;
    case 'FREQ=WEEKLY':
      repeatType = CalendarRepetition.WEEKLY;
      repeatEvery = Number(values.find((value) => value.includes('INTERVAL'))?.replace('INTERVAL=', '')) || 1;
      selectedDays =
        values
          .find((value) => value.includes('BYDAY'))
          ?.replace('BYDAY=', '')
          .split(',') || [];
      break;
    case 'FREQ=MONTHLY':
      repeatType = CalendarRepetition.MONTHLY;
      if (hasPosition) {
        position = values.find((value) => value.includes('BYSETPOS'))?.replace('BYSETPOS=', '') || '';
        const byDayValue = values.find((value) => value.includes('BYDAY'))?.replace('BYDAY=', '') || '';

        // Check if BYDAY contains combined position+day format (e.g., "1MO")
        if (byDayValue && byDayValue.length > 2) {
          // Extract position and day from combined format like "1MO"
          const extractedPosition = byDayValue.substring(0, byDayValue.length - 2);
          const extractedDay = byDayValue.substring(byDayValue.length - 2);

          // Use the extracted values instead of BYSETPOS
          position = extractedPosition;
          selectedDay = extractedDay;
        } else {
          // Fallback to separate BYSETPOS and BYDAY
          selectedDay = byDayValue;
        }

        if (values.find((value) => value.includes('INTERVAL'))) {
          repeatEvery = Number(values.find((value) => value.includes('INTERVAL'))?.replace('INTERVAL=', '')) || 1;
        }
      } else {
        selectedDay = values.find((value) => value.includes('BYMONTHDAY'))?.replace('BYMONTHDAY=', '') || '';
        if (values.find((value) => value.includes('INTERVAL'))) {
          repeatEvery = Number(values.find((value) => value.includes('INTERVAL'))?.replace('INTERVAL=', '')) || 1;
        }
      }
      break;
    case 'FREQ=YEARLY':
      repeatType = CalendarRepetition.YEARLY;
      repeatEvery = Number(values.find((value) => value.includes('INTERVAL'))?.replace('INTERVAL=', '')) || 1;
      if (hasPosition) {
        position = values.find((value) => value.includes('BYSETPOS'))?.replace('BYSETPOS=', '') || '';
        const byDayValue = values.find((value) => value.includes('BYDAY'))?.replace('BYDAY=', '') || '';

        // Check if BYDAY contains combined position+day format (e.g., "1MO")
        if (byDayValue && byDayValue.length > 2) {
          // Extract position and day from combined format like "1MO"
          const extractedPosition = byDayValue.substring(0, byDayValue.length - 2);
          const extractedDay = byDayValue.substring(byDayValue.length - 2);

          // Use the extracted values instead of BYSETPOS
          position = extractedPosition;
          selectedDay = extractedDay;
        } else {
          // Fallback to separate BYSETPOS and BYDAY
          selectedDay = byDayValue;
        }

        selectedMonth = Number(values.find((value) => value.includes('BYMONTH'))?.replace('BYMONTH=', '')) || 1;
      } else {
        selectedDay = values.find((value) => value.includes('BYMONTHDAY'))?.replace('BYMONTHDAY=', '') || '';
        selectedMonth = Number(values.find((value) => value.includes('BYMONTH'))?.replace('BYMONTH=', '')) || 1;
      }
      break;
    default:
      break;
  }

  if (values.some((v) => v.startsWith('COUNT='))) {
    const countValue = values.find((v) => v.startsWith('COUNT='));
    if (!countValue) return null;
    return {
      repeatType,
      repeatEvery,
      selectedDays,
      selectedDay,
      selectedMonth,
      position,
      iterations: Number(countValue.replace('COUNT=', '')),
    };
  }

  if (values.some((v) => v.startsWith('UNTIL='))) {
    const untilValue = values.find((v) => v.startsWith('UNTIL='));
    if (!untilValue) return null;

    try {
      // Parse the UNTIL value which is in format YYYYMMDDTHHMMSSZ
      const untilStr = untilValue.replace('UNTIL=', '');
      const year = parseInt(untilStr.substring(0, 4));
      const month = parseInt(untilStr.substring(4, 6)) - 1;
      const day = parseInt(untilStr.substring(6, 8));
      const hours = parseInt(untilStr.substring(9, 11));
      const minutes = parseInt(untilStr.substring(11, 13));
      const seconds = parseInt(untilStr.substring(13, 15));

      // Create date in UTC
      const date = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
      if (isNaN(date.getTime())) {
        console.error('Invalid date parsed from UNTIL:', untilStr);
        return { repeatType, repeatEvery, selectedDays, selectedDay, selectedMonth, position };
      }

      return {
        repeatType,
        repeatEvery,
        selectedDays,
        selectedDay,
        selectedMonth,
        position,
        endDate: date,
      };
    } catch (error) {
      console.error('Error parsing UNTIL date:', error);
      return { repeatType, repeatEvery, selectedDays, selectedDay, selectedMonth, position };
    }
  }

  return { repeatType, repeatEvery, selectedDays, selectedDay, selectedMonth, position };
};
